import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Button, Chip, Container, Stack, Typography } from "@mui/material";
import blogVideos from "../data/blogVideos.json";
import Footer from "../componentsPeopletech/Footer/Footer";

const BlogVideoPage = () => {
  const { slug } = useParams();
  const baseUrl = "https://www.peoplefortech.com";
  const video = blogVideos.find((item) => item.slug === slug);

  useEffect(() => {
    const isPrerender = typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent);
    if (isPrerender) {
      return undefined;
    }

    let script = document.getElementById("tiktok-embed-script");
    if (!script) {
      script = document.createElement("script");
      script.src = "https://www.tiktok.com/embed.js";
      script.async = true;
      script.id = "tiktok-embed-script";
      document.body.appendChild(script);
    } else if (window.tiktokEmbed && window.tiktokEmbed.lib) {
      window.tiktokEmbed.lib.render();
    }

    return undefined;
  }, []);

  if (!video) {
    return (
      <Container maxWidth="md" sx={{ py: 10 }}>
        <Helmet>
          <title>Artículo no encontrado - PeopleForTech</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <Typography variant="h3" gutterBottom>
          Artículo no encontrado
        </Typography>
        <Button component={RouterLink} to="/blog" variant="contained" sx={{ textTransform: "none" }}>
          Volver al blog
        </Button>
      </Container>
    );
  }

  const pageUrl = `${baseUrl}/blog/${video.slug}`;
  const keywords = [video.focusKeyword, ...video.longtailKeywords].join(", ");
  const embedHtml = `<blockquote class="tiktok-embed" cite="${video.tiktokUrl}" data-video-id="${video.id}" style="max-width: 720px;min-width: 325px;">
    <section>
      <a target="_blank" title="@peoplefortech" href="https://www.tiktok.com/@peoplefortech?refer=embed">@peoplefortech</a>
      <a target="_blank" href="${video.tiktokUrl}">${video.title}</a>
    </section>
  </blockquote>`;

  return (
    <>
      <Helmet>
        <title>{`${video.title} | Blog PeopleForTech`}</title>
        <meta name="description" content={video.description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={video.title} />
        <meta property="og:description" content={video.description} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={video.title} />
        <meta property="twitter:description" content={video.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: video.title,
            description: video.description,
            mainEntityOfPage: pageUrl,
            url: pageUrl,
            about: [video.focusKeyword, ...video.longtailKeywords],
            keywords,
            publisher: {
              "@type": "Organization",
              name: "PeopleForTech",
              url: baseUrl
            },
            author: {
              "@type": "Organization",
              name: "PeopleForTech"
            },
            video: {
              "@type": "VideoObject",
              embedUrl: video.tiktokUrl,
              url: video.tiktokUrl,
              name: video.title,
              description: video.description
            }
          })}
        </script>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Stack spacing={3}>
          <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, fontWeight: 700 }}>
            {video.title}
          </Typography>

          <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
            {video.description}
          </Typography>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip label={`Keyword foco: ${video.focusKeyword}`} />
            {video.longtailKeywords.map((keyword) => (
              <Chip key={keyword} label={keyword} variant="outlined" />
            ))}
          </Stack>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              display: "flex",
              justifyContent: "center",
              backgroundColor: "#fff"
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: embedHtml }} />
          </Box>

          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Button component={RouterLink} to="/blog" variant="outlined" sx={{ textTransform: "none" }}>
              Volver al blog
            </Button>
            <Button component={RouterLink} to="/geo-para-chatgpt-gemini" variant="contained" sx={{ textTransform: "none", backgroundColor: "#111" }}>
              Ver ejemplo GEO
            </Button>
            <Button component={RouterLink} to="/contact" variant="contained" sx={{ textTransform: "none", backgroundColor: "#1e2a5a" }}>
              Contactar para asesoría IA
            </Button>
          </Stack>
        </Stack>
      </Container>
      <Footer />
    </>
  );
};

export default BlogVideoPage;
